import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { getPendingHostListingInviteId, getPendingInviteId, getPendingVenueReferralData, getPendingStaffInviteToken } from '../utils/deepLinkHandler';
import { useBackTarget } from '../hooks/useBackTarget';
import { getPendingPartnerIntent } from '../utils/partnerIntent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerifyEmailPage({ onLogin }) {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const email = localStorage.getItem('pendingVerificationEmail') || '';

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 4) {
      toast.error('Please enter a 4-digit code');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-email`, { email, code });

      // Update stored user as verified
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.email_verified = true;
      localStorage.setItem('user', JSON.stringify(storedUser));
      localStorage.removeItem('pendingVerificationEmail');

      if (onLogin) onLogin(storedUser);
      toast.success('Email verified!');

      const pendingInviteId = getPendingInviteId();
      const pendingHostListingInviteId = getPendingHostListingInviteId();
      const pendingVenueReferral = getPendingVenueReferralData();
      const pendingStaffInviteToken = getPendingStaffInviteToken();
      // Priority: deep-link invites > partner intent > guest home.
      // Deep-link paths bypass the /select-role picker entirely so an
      // invitee never lands on the wrong shell (Feb 2026 fix).
      const partnerIntent = getPendingPartnerIntent();
      if (pendingStaffInviteToken) navigate(`/staff-invite/${pendingStaffInviteToken}`, { replace: true });
      else if (pendingInviteId) navigate(`/invite/${pendingInviteId}`, { replace: true });
      else if (pendingHostListingInviteId) navigate('/host-intro-1', { replace: true });
      else if (pendingVenueReferral) navigate('/venue/intro', { replace: true });
      else if (partnerIntent === 'host') navigate('/host-verify-listing', { replace: true });
      else if (partnerIntent === 'venue') navigate('/venue/onboarding/1', { replace: true });
      // Fresh signup with no partner intent AND no deep-link — force
      // the role picker so a direct /register user picks Guest / Host /
      // Venue instead of getting silently dumped into the guest app.
      else navigate('/select-role', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-code`, { email });
      toast.success('Verification code resent!');
    } catch (err) {
      toast.error('Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      className="app-shell"
      style={{
        minHeight: '100vh',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        position: 'relative',
      }}
    >
      {/* Back arrow */}
      <button
        onClick={goBack}
        aria-label="Go back"
        style={{
          position: 'absolute',
          top: 20,
          left: 20,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 4,
        }}
      >
        <ChevronLeft size={28} color="var(--text)" strokeWidth={1.5} />
      </button>

      {/* Logo */}
      <RouteTransitionLogo width={140} height={125} dataTestId="verify-email-shared-logo" style={{ marginTop: 60 }} />

      {/* Verify your E-mail - Amarante, #303030 */}
      <h1
        style={{
          fontFamily: "'Amarante', serif",
          fontSize: 26,
          fontWeight: 400,
          color: 'var(--text)',
          textAlign: 'center',
          marginTop: 12,
        }}
      >
        Verify your E-mail
      </h1>

      {/* Spacer */}
      <div style={{ flex: 1, minHeight: 60 }} />

      {/* 4-digit code input */}
      <form
        onSubmit={handleVerify}
        style={{
          width: '100%',
          maxWidth: 300,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div style={{ width: '100%', marginBottom: 50, textAlign: 'center' }}>
          <input
            type="text"
            value={code}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 4);
              setCode(val);
            }}
            placeholder="4-digit code"
            maxLength={4}
            data-testid="verify-code-input"
            style={{
              width: '100%',
              border: 'none',
              borderBottom: '2px dotted var(--text-3)',
              outline: 'none',
              background: 'transparent',
              fontFamily: "'Inria Serif', serif",
              fontSize: 22,
              color: 'var(--text)',
              textAlign: 'center',
              paddingBottom: 8,
              paddingTop: 4,
              letterSpacing: '8px',
            }}
          />
        </div>

        {/* Continue button */}
        <motion.button
          type="submit"
          disabled={loading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          data-testid="verify-submit-button"
          className="premium-pill-button"
          style={{
            width: 220,
            padding: '13px 0',
            fontFamily: "'Inria Serif', serif",
            fontSize: 20,
            fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
            marginBottom: 28,
          }}
        >
          {loading ? 'Verifying...' : 'Continue'}
        </motion.button>
      </form>

      {/* Resend link - purple color */}
      <button
        onClick={handleResend}
        disabled={resending}
        style={{
          fontFamily: "'Inria Serif', serif",
          fontSize: 20,
          fontWeight: 400,
          color: 'var(--accent-purple)',
          background: 'none',
          border: 'none',
          cursor: resending ? 'not-allowed' : 'pointer',
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
          padding: '6px 12px',
          opacity: resending ? 0.6 : 1,
        }}
      >
        {resending ? 'Sending...' : 'Resend'}
      </button>

      {/* Spacer */}
      <div style={{ flex: 1, minHeight: 60 }} />
    </motion.div>
  );
}
